import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import { ReplaySubject, Subject } from 'rxjs/';

import { CommunicationService } from './communication.service';
import { HCPGroupService } from './hcpgroup.service';
import { HcpQuestionnaireService } from './hcp-questionnaire.service';

import { Study } from '../models/study';
import { User } from '../models/user';
import { Alarm } from '../models/alarm';
import { Questionnaire } from '../models/questionnaire';
import { Answersheet } from '../models/answersheet';
import { BasicError } from '../models/error/error';

@Injectable()
export class AlarmService {
  private isInit = false;
  private initReplay: Subject<any> = null;
  private mainReplay: Subject<any> = new Subject();
  private refreshTimestamp = null;
  private refreshTimer = null;
  private alarmIds = {};
  private alarms: Array<Alarm> = [];
  private warnings: Array<Alarm> = [];
  private studies: Array<Study> = [];
  private questionnaires = {};
  private usersFilter = {}; // Depending on study
  private allUsers: Array<User> = []; // All Users
  private userIdMap = {}; // Quick fix to find if user already exists
  private evaluateURL = '/api/v1/studies/##sid##/members/##mid##/answersheets/##aid##/evaluate';

  private getAlarmsOngoing = false;

  constructor(private hcpgroup: HCPGroupService,
    private questionnare: HcpQuestionnaireService, private communication: CommunicationService) { }

  /**
   * This function initializes this service. After initializing
   * all studies and members are loaded and can be registered
   */
  public initService(): Subject<any> {
    if (this.initReplay !== null) {
      // Init is ongoing
      return this.initReplay;
    }

    this.initReplay = new Subject(); // Init replay

    // Getting all studies the current user is in
    this.hcpgroup.getCollaboratingStudies().subscribe(json => {
      if (json instanceof BasicError) {
        console.log('-------------AlarmService----------: Error occured while getting studies.');
        console.log(json);
        this.initReplay.next(json);
        this.initReplay = null;
        return;
      }

      // Studies were returned, now get the members of each study
      let memberDone = false;
      let questionnairesDone = false;
      if (json instanceof Array && json.length > 0) {
        this.initAllMembers(json).subscribe(done => {
          if (this.allUsers.length === 0) {
            return;
          }
          if (done) {
            memberDone = true;
            if (questionnairesDone) {
              this.isInit = true;
              this.initReplay.next(true);
              this.initReplay = null;
              this.getAlarms();
            }
          } else {
            console.log('---AlarmService----: Members could not be retrieved');
            this.initReplay.next(false);
            this.initReplay = null;
          }

        });

        // Getting all necessary questionnaires
        const study = new Study('1', 'Sivantos Main', 'Sivantos Main');
        this.initGetQuestionnaires([study]).subscribe(done => {
          if (done) {
            questionnairesDone = true;
            if (memberDone) {
              this.isInit = true;
              this.initReplay.next(true);
              this.initReplay = null;
              this.getAlarms();
            }
          } else {
            console.log('---AlarmService----: Questionnaires could not be retrieved');
            this.initReplay.next(false);
            this.initReplay = null;
          }
        });
      }
    });

    return this.initReplay;
  }

  /**
   * Function to get all members of all studies recursive
   * @param studies all studies that should be searched for
   * @param studyCounter the counter whichh starts at 0
   * @param retReplay replay for returning the answer
   */
  public initAllMembers(studies, studyCounter = 0, retReplay: Subject<any> = null): Subject<any> {
    if (retReplay === null) {
      retReplay = new Subject();
    }

    const study = studies[studyCounter]; // Get first study
    this.studies.push(study);

    this.initGetMembers(study).subscribe(users => {
      // Got some users, get them into array of allUsers
      for (const user of users) {
        if (user instanceof User && this.userIdMap[user.getID().toString()] === undefined) {
          this.userIdMap[user.getID().toString()] = user;
          this.allUsers.push(user);
        }
      }

      // Get next study and increment studyCounter
      studyCounter = studyCounter + 1;
      if (studyCounter < studies.length) {
        this.initAllMembers(studies, studyCounter, retReplay);
      } else {
        // All studies are finished
        retReplay.next(true);
      }
    });

    return retReplay;
  }

  /**
   * this function gets all members of a specific study
   * @param study Study to get members from
   */
  public initGetMembers(study): Subject<any> {
    const retReplay = new Subject();

    this.hcpgroup.getMembersOfStudy(study.getId()).subscribe(dd => {
      if (dd instanceof BasicError) {
        console.log('-------AlarmService-------: Error in getting members of study [id]:' + study.getId());
        retReplay.next([]);
      } else {
        if (this.usersFilter[study.getId().toString()] === undefined) {
          this.usersFilter[study.getId().toString()] = dd;
        } else {
          this.usersFilter[study.getId().toString()] = this.usersFilter[study.getId().toString()].concat(dd);
        }

        retReplay.next(dd);
      }
    });

    return retReplay;
  }

  /**
   * This function gets all necessary questionnaires (only multiple)
   * @param studies studies to get questionnaires from
   * @param studyCounter counter for functionality (starts with 0)
   * @param retReplay replay to return answer
   */
  public initGetQuestionnaires(studies, studyCounter = 0, retReplay = null): Subject<any> {
    if (retReplay === null) {
      retReplay = new Subject();
    }

    const study = studies[studyCounter];
    this.initGetQuestionnairesOfStudy(study).subscribe(json => {
      if (json.length > 0) {
        this.questionnaires[study.getId().toString()] = json;
      }
      // Increment counter and recursive

      studyCounter = studyCounter + 1;
      if (studyCounter < studies.length) {
        this.initGetQuestionnaires(studies, studyCounter, retReplay);
      } else {
        retReplay.next(true);
      }
    });

    return retReplay;
  }

  /**
   * this function returns all questionnaires of a specific study
   * @param study study to get questionnaires from
   */
  public initGetQuestionnairesOfStudy(study: Study): Subject<any> {
    const retReplay = new Subject();

    // Getting questionnaires of one study
    this.questionnare.getQuestionnairesOfStudy(study.getId()).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('---AlarmService----: Failed to get Questionnaire of Study [id]: ' + study.getId());
        retReplay.next([]);
        return;
      }

      if (json.length === 0) {
        retReplay.next([]);
      }

      // Getting detailed questionnaires
      this.questionnare.getQuestionnairesDetails(json).subscribe(dd => {
        if (dd instanceof BasicError) {
          console.log('----AlarmService----: Failed to get detailed data of questionnaire');
          retReplay.next([]);
          return;
        }

        // Filtering only questionnaire which are multiple
        const retArr = [];
        for (const quest of dd) {
          if (quest instanceof Questionnaire && quest.isMultiple()) {
            retArr.push(quest);
          }
        }

        retReplay.next(retArr);
      });
    });

    return retReplay;
  }

  /**
   * Function to check if service is initialized
   */
  public isInitialized() {
    return this.isInit;
  }

  /**
   * Function to get the mainReplay (when something changes),
   * the changes will be returned immediately
   */
  public observe(): Subject<any> {
    if (!this.isInit) {
      this.initService();
    }

    return this.mainReplay;
  }

  /**
   * Refreshing the current alarms
   * @param all all (last 3 answersheets)
   */
  public refresh(all: Boolean = false) {
    if (all) {
      this.refreshTimestamp = null;
    }

    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
    }

    this.getAlarms();

  }

  /**
   * This function does the main work of getting the alarms
   */
  public getAlarms() {
    if (!this.isInit) {
      this.initService();
      return;
    }

    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
    }

    if (this.getAlarmsOngoing) {
      return;
    }

    this.getAlarmsOngoing = true;

    this.alarms = [];
    this.warnings = [];
    this.alarmIds = {};

    this.getAlarmsOfMembers().subscribe(json => {
      // getting alarms is done
      this.getAlarmsOngoing = false;
      this.mainReplay.next(true);
      const self = this;
      this.refreshTimer = setTimeout(function () {
        self.getAlarms();
      }, 60000);
    });
  }

  /**
   * This function gets all answersheets and evaluates this
   * @param memberCount which member is considered
   * @param questionnaireCount which questionnaire is considered
   * @param retReplay replay to get request answer to when finished
   */
  public getAlarmsOfMembers(memberCount = 0, questionnaireCount = 0, studyC = 0, retReplay = null): Subject<any> {
    if (retReplay === null) {
      retReplay = new Subject();
    }

    let study = this.studies[studyC];
    if (this.questionnaires[study.getId().toString()] === undefined || this.questionnaires[study.getId().toString()].length === 0) {
      studyC = studyC + 1;

      if (studyC >= this.studies.length) {
        memberCount = memberCount + 1;
        studyC = 0;
      }

      if (memberCount >= this.allUsers.length) {
        retReplay.next(true);
        return;
      } else {
        this.getAlarmsOfMembers(memberCount, questionnaireCount, studyC, retReplay);
        return retReplay;
      }
    }


    // Getting actual member and questionnaire
    const member = this.allUsers[memberCount];
    const quest = this.questionnaires[study.getId().toString()][questionnaireCount];

    // Getting answersheet of this user and questionnaire
    this.getAnswersheetsOfMember(study, quest, member).subscribe(json => {
      if (json) {
        // Answer of this user was retrieved successsful
      } else {
        console.log('---AlarmService---: Could not get answers of member');
      }

      questionnaireCount = questionnaireCount + 1;
      // Check if all questionnaires were considered for one member
      if (questionnaireCount >= this.questionnaires[study.getId().toString()].length) {
        studyC = studyC + 1;
        questionnaireCount = 0;
      }

      // Check if all studies were considered for this member
      if (studyC >= this.studies.length) {
        memberCount = memberCount + 1;
        studyC = 0;
      } else {
        study = this.studies[studyC];
      }

      // Check if all members were considered
      if (memberCount >= this.allUsers.length) {
        retReplay.next(true);
        return;
      } else {
        this.getAlarmsOfMembers(memberCount, questionnaireCount, studyC, retReplay);
      }
    });


    return retReplay;
  }

  /**
   * Getting answersheet of a specific member
   * @param study study
   * @param quest questionnaire
   * @param member member
   */
  public getAnswersheetsOfMember(study: Study, quest: Questionnaire, member: User): Subject<any> {
    const retReplay = new Subject();

    this.questionnare.getAnswersheetsOfMemberAlternative('1', quest.getId(), member.getID(), 0, 3, '-id').subscribe(json => {
      if (json instanceof BasicError) {
        console.log('-----AlarmService-----: Failed to get answersheet of user');
        console.log(json);
        retReplay.next(false);
        return;
      }

      if (json.length > 0) {
        this.evaluateAnswersheetsOfMember(study, member, json, quest).subscribe(dd => {
          // evaluation done
          // console.log('---AlarmService----: Evaluation of member done [memberId]:' + member.getID());
          retReplay.next(true);
        });
      } else {
        // console.log('----AlarmService----: Member does not have answers [memberId]:' + member.getID())
        retReplay.next(true);
      }
    });

    return retReplay;
  }

  public evaluateAnswersheetsOfMember(study: Study, member: User,
    answers: Array<Answersheet>, quest: Questionnaire, sheetCount = 0, retReplay: Subject<any> = null): Subject<any> {
    if (retReplay === null) {
      retReplay = new Subject();
    }



    // Evaluate answersheet
    const answer = answers[sheetCount];

    this.evaluateAnswersheet(study.getId(), member.getID(), answer, quest).subscribe(dd => {
      if (dd) {
        // was successful
      } else {
        // wasn't successful
      }
      sheetCount = sheetCount + 1;

      if (sheetCount >= answers.length) {
        // All answers were evaluated
        retReplay.next(true);
      } else {
        this.evaluateAnswersheetsOfMember(study, member, answers, quest, sheetCount, retReplay);
      }
    });

    return retReplay;
  }


  public evaluateAnswersheet(studyId: String, memberId: String, answer: Answersheet, quest: Questionnaire): Subject<any> {
    const retReplay = new Subject();

    let url = this.evaluateURL.replace('##sid##', studyId.toString());
    url = url.replace('##mid##', memberId.toString());
    url = url.replace('##aid##', answer.getId().toString());

    // Call the evaluation route
    this.communication.get(url).subscribe(json => {
      if (json instanceof BasicError) {
        // Failed evaluation
        console.log('---AlarmService----: Failed to evaluate answersheet');
        console.log(json);
        retReplay.next(false);
        return;
      }

      // Getting feedbacks of the evaluation
      const id = json.data.id;
      const answerId = json.data.attributes.answersheet_id;
      const collectedAt = json.data.attributes.collected_at;

      for (const feedback of json.data.attributes.feedbacks) {
        // Get feedback
        const feedId = feedback.id;
        const result = feedback.result;
        const rule = feedback.rule;
        const headline = feedback.true.headline;
        const text = feedback.true.text;
        let isPrivate = false;
        if (feedback.is_private !== undefined) { isPrivate = feedback.is_private; }

        // Create Feedback
        const f = new Alarm(feedId, answerId);
        (isPrivate) ? f.setAlarm(result) : f.setWarning(result);
        f.setCollectedAt(collectedAt);
        f.setResult(result);
        f.setRule(rule);
        f.setHeadline(headline);
        f.setText(text);
        f.setUser(this.userIdMap[memberId.toString()]);
        f.setStudyId(studyId);
        f.setQuestionnaireId(quest.getId());

        // Add it to the corresponding array
        if (result && this.alarmIds[answerId] === undefined) {
          (isPrivate) ? this.alarms.push(f) : this.warnings.push(f);
        }
      }
      this.alarmIds[answerId] = true;

      retReplay.next(true);
    });

    return retReplay;
  }

  /**
   * Getting all currently loaded alarms (if observer registered later after some
   * alarms were already fetched)
   */
  public getCurrentAlarms() {
    return {
      'alarms': this.alarms,
      'warnings': this.warnings
    };
  }

}



// WEBPACK FOOTER //
// C:/Users/andyw/Documents/Uni/Master/Masterprojekt/Angular/src/app/services/alarm.service.ts